import {
  GoogleAuthProvider,
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut as fbSignOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { getApp } from './app';
import { isConfigured } from './config';
import { EMULATOR_HOST, EMULATOR_PORTS, emulatorsEnabled } from './emulator';
import { fetchMyAccess } from '../admin/access-api';

/**
 * Service d'authentification Firebase — **admin only** depuis le pivot
 * e-commerce (cf. cahier-des-charges-ecommerce-v01.md §2.2) : les clients de
 * la boutique n'ont pas de compte Perleur (guest checkout Shopify). Seul
 * l'admin se connecte, via Google, pour accéder au générateur de produits.
 *
 * Politique :
 *  - Si la config Firebase n'est pas remplie, `auth.user` reste `null` et
 *    `auth.configured` est `false` — l'UI affiche un message au lieu de
 *    crasher (utile en dev local avant config + en CI).
 *  - Sign-in via popup Google (plus simple que redirect, fonctionne en SPA
 *    sans setup côté server).
 *  - `onAuthStateChanged` synchronise l'état avec le cache Firebase (le
 *    user reste connecté après refresh tant que le token n'expire pas).
 */

/**
 * Rôle utilisateur — déterminé par l'ALLOWLIST D'E-MAILS côté serveur
 * (callable `getMyAccess`, cf. functions/admin-access), pilotable depuis
 * l'admin. Plus de custom claim ni de script bootstrap.
 *  - `admin` : e-mail présent dans l'allowlist
 *  - `null`  : non autorisé
 *
 * **Important** : purement informatif côté client (gating UI). La vraie
 * autorisation est refaite côté serveur (`assertAdmin`) sur chaque callable
 * sensible — infalsifiable depuis le navigateur.
 */
export type UserRole = 'admin';

/** Représentation simplifiée d'un utilisateur pour l'UI. */
export type AuthUser = {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
};

function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    photoURL: user.photoURL,
  };
}

// État réactif global (singleton)
let user = $state<AuthUser | null>(null);
let loading = $state<boolean>(true);
let errorMsg = $state<string>('');
/**
 * Rôle courant lu depuis le claim `role` du token Firebase Auth.
 * Récupéré async après que `onAuthStateChanged` ait fourni le user.
 * `null` pendant le temps de fetch + pour tous les users standards.
 */
let role = $state<UserRole | null>(null);
/** Vrai pendant la résolution de l'accès admin (appel serveur getMyAccess). */
let roleLoading = $state<boolean>(false);

let authInstance: Auth | null = null;
let initialized = false;

async function refreshAccess(): Promise<void> {
  roleLoading = true;
  try {
    const access = await fetchMyAccess();
    role = access.admin ? 'admin' : null;
  } catch (err) {
    console.error('[auth] refreshAccess failed:', err);
    role = null;
  } finally {
    roleLoading = false;
  }
}

function ensureInit(): void {
  if (initialized) return;
  initialized = true;
  const app = getApp();
  if (!app) {
    loading = false;
    return;
  }
  authInstance = getAuth(app);
  if (emulatorsEnabled()) {
    connectAuthEmulator(authInstance, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
      disableWarnings: true,
    });
  }
  onAuthStateChanged(authInstance, (fbUser) => {
    user = toAuthUser(fbUser);
    loading = false;
    if (fbUser) {
      void refreshAccess();
    } else {
      role = null;
      roleLoading = false;
    }
  });
}

export const auth = {
  /** L'utilisateur courant, ou `null` si déconnecté / config absente. */
  get user(): AuthUser | null {
    ensureInit();
    return user;
  },
  /** Vrai pendant la première résolution de `onAuthStateChanged`. */
  get loading(): boolean {
    ensureInit();
    return loading;
  },
  /** Vrai si la config Firebase est présente (clés env remplies). */
  get configured(): boolean {
    return isConfigured();
  },
  /** Dernier message d'erreur sign-in, vide si OK. */
  get error(): string {
    return errorMsg;
  },
  /** Rôle courant (`admin` ou `null`). */
  get role(): UserRole | null {
    ensureInit();
    return role;
  },
  /** Vrai si l'e-mail du user est dans l'allowlist admin (vérifié serveur). */
  get isAdmin(): boolean {
    return role === 'admin';
  },
  /** Vrai pendant la vérification de l'accès admin (évite un flash « refusé »). */
  get roleLoading(): boolean {
    ensureInit();
    return roleLoading;
  },

  /**
   * Recontrôle l'accès admin auprès du serveur (allowlist). À appeler après
   * qu'un accès vient d'être accordé, ou pour rafraîchir l'état.
   */
  async refreshClaims(): Promise<void> {
    ensureInit();
    if (!authInstance?.currentUser) return;
    await refreshAccess();
  },

  /** Émulateurs actifs ? (affiche le bouton de connexion de test sur /login). */
  get emulated(): boolean {
    return emulatorsEnabled();
  },

  /**
   * DEV/TEST UNIQUEMENT (émulateur Firebase) : connecte un admin FACTICE sans
   * Google réel, via un jeton Google émulé (`email_verified: true`). No-op hors
   * émulateur — ne PEUT donc pas fonctionner en prod. Permet de piloter l'admin
   * en test (Chrome DevTools) sans le vrai compte Google.
   */
  async signInTestAdmin(email = 'olivierpallatier@gmail.com'): Promise<void> {
    ensureInit();
    if (!emulatorsEnabled()) {
      console.error('[auth] signInTestAdmin ignoré : émulateurs désactivés.');
      return;
    }
    if (!authInstance) return;
    errorMsg = '';
    try {
      const token = JSON.stringify({ sub: `test-${email}`, email, email_verified: true });
      await signInWithCredential(authInstance, GoogleAuthProvider.credential(token));
    } catch (err) {
      errorMsg = err instanceof Error ? err.message : 'Échec de la connexion de test.';
      console.error('[auth] signInTestAdmin failed:', err);
    }
  },

  async signInWithGoogle(): Promise<void> {
    ensureInit();
    if (!authInstance) {
      errorMsg = 'Firebase non configuré.';
      return;
    }
    errorMsg = '';
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(authInstance, provider);
      // user state mis à jour par onAuthStateChanged
    } catch (err) {
      // Cas courant : popup fermée par l'utilisateur → pas une vraie erreur
      const code = (err as { code?: string }).code;
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      errorMsg = err instanceof Error ? err.message : 'Échec de la connexion.';
      console.error('[auth] Sign-in failed:', err);
    }
  },

  async signOut(): Promise<void> {
    ensureInit();
    if (!authInstance) return;
    try {
      await fbSignOut(authInstance);
    } catch (err) {
      console.error('[auth] Sign-out failed:', err);
    }
  },
};
